import type { SymptomKey } from './store'

/**
 * The safety layer.
 *
 * NabuSu is used by people in benzodiazepine withdrawal. That population has a
 * real, non-theoretical risk of seizure, delirium and suicide. So two rules are
 * hard-wired here rather than left to prompt text or copywriting:
 *
 *   1. Certain symptoms are never handled by the app. They interrupt whatever
 *      screen you are on and send you to emergency care.
 *   2. Nothing in this codebase ever proposes a dose, a schedule or a taper
 *      rate. The ledger records what a prescriber decided. It does not advise.
 */

/** Symptoms that mean "stop using this app and get medical help now." */
export const RED_FLAG_SYMPTOMS: readonly SymptomKey[] = [
  'seizure',
  'hallucination',
  'confusion',
  'fever',
] as const

export function hasRedFlag(symptoms: readonly SymptomKey[]): boolean {
  return symptoms.some((s) => RED_FLAG_SYMPTOMS.includes(s))
}

/**
 * Crisis language detection for the companion chat.
 *
 * Deliberately tuned to over-trigger. A false positive costs someone one extra
 * screen they can dismiss. A false negative costs more than this app is worth.
 */
const CRISIS_PATTERNS: RegExp[] = [
  // English
  /\b(kill|killing)\s+myself\b/i,
  /\bend(ing)?\s+(my|it)\s*(life|all)?\b/i,
  /\b(suicide|suicidal)\b/i,
  /\bwant\s+to\s+die\b/i,
  /\bdon'?t\s+want\s+to\s+(live|be here|wake up)\b/i,
  /\bno\s+(point|reason)\s+(in\s+)?(living|going on)\b/i,
  /\b(overdose|od)\s+(on|myself)\b/i,
  /\btake\s+(all|every)\s+(the\s+)?pills\b/i,
  /\bhurt(ing)?\s+myself\b/i,
  /\bbetter\s+off\s+(dead|without me)\b/i,
  // Persian
  /خودکشی/,
  /خودم\s*را?\s*بکشم/,
  /می.?خواهم\s*بمیرم/,
  /میخوام\s*بمیرم/,
  /نمی.?خواهم\s*زنده\s*بمانم/,
  /نمیخوام\s*زنده\s*بمونم/,
  /به\s*زندگی.?ام?\s*پایان/,
  /دیگر?\s*نمی.?کشم/,
  /همه.?ی?\s*قرص.?ها.?\s*را?\s*بخورم/,
  /به\s*خودم\s*آسیب/,
]

export function detectsCrisis(text: string): boolean {
  const normalised = text
    .replace(/[ي]/g, 'ی') // Arabic yeh → Persian yeh
    .replace(/[ك]/g, 'ک') // Arabic kaf → Persian kaf
    .replace(/‌/g, ' ') // ZWNJ → space, so "نمی‌خواهم" matches
  return CRISIS_PATTERNS.some((re) => re.test(normalised))
}

/**
 * Physical-emergency language in free text. Separate from crisis: the response
 * is different (emergency department vs. a human to talk to).
 */
const MEDICAL_EMERGENCY_PATTERNS: RegExp[] = [
  /\b(had|having)\s+a\s+seizure\b/i,
  /\bconvulsion/i,
  /\bfit(ting)?\s+again\b/i,
  /\bseeing\s+things\b/i,
  /\bhearing\s+voices\b/i,
  /\bhallucinat/i,
  /\bcan'?t\s+stop\s+(vomiting|throwing up)\b/i,
  /تشنج/,
  /توهم\s*(می.?بینم|دارم)/,
  /صدا.?ها.?یی?\s*می.?شنوم/,
  /استفراغ.*بند\s*نمی/,
]

export function detectsMedicalEmergency(text: string): boolean {
  const normalised = text.replace(/‌/g, ' ')
  return MEDICAL_EMERGENCY_PATTERNS.some((re) => re.test(normalised))
}

/**
 * The one guardrail applied to any AI response before it reaches a user:
 * if a model ever produces dosing guidance, drop the response entirely.
 * We would rather say nothing than say "try half a milligram".
 */
// No `\b` on the Persian patterns. JavaScript's word boundary is ASCII-only:
// Persian letters are non-word characters to it, so `\bمیلی` only matches when
// the preceding character is an ASCII letter or digit — which never happens in
// a real Persian sentence, where the word is preceded by a space or starts the
// string. With the boundary in place this filter silently never fired, in the
// product's primary language, against exactly the advice rule 1 exists to stop.
//
// The English verbs accept their -ing and -s forms too. "Reduce the dose" was
// caught and "reducing the dose" was not, which is not a distinction anyone
// writing to a person in withdrawal is making.
const DOSING_PATTERNS: RegExp[] = [
  /\b\d+(\.\d+)?\s*(mg|milligram)/i,
  /\b(cut(s|ting)?|reduc(e|es|ing)|lower(s|ing)?|halv(e|es|ing)|drop(s|ping)?)\s+(your|the)\s+dose/i,
  /\bstop(s|ping)?\s+taking\b/i,
  /\btaper(s|ing)?\s+(by|at|to)\b/i,
  /میلی.?گرم/,
  /دوز.*(کم|نصف|قطع)\s*کن/,
  /مصرف.*قطع\s*کن/,
]

export function containsDosingAdvice(text: string): boolean {
  const normalised = text.replace(/‌/g, ' ')
  return DOSING_PATTERNS.some((re) => re.test(normalised))
}
